import type { ReportTemplate, Node } from "../types/template.js";
import type { DataSource, FieldSchema } from "../types/data.js";
import type { Theme } from "../types/theme.js";
import type { BlockRegistry } from "../registry/registry.js";
import { bindingSpecsFor } from "../registry/registry.js";
import type { BindingSpec, ValidateContext } from "../registry/block-definition.js";
import { createSchemaScope, resolvePathSchema, typeCheck, SchemaScope } from "../expr/typecheck.js";
import { parseExpression } from "../expr/parser.js";
import { compileCondition } from "../expr/condition.js";
import { extractTemplateTokens, isRichTextDoc, richTextTokens } from "../richtext/model.js";
import { isTokenRef, missingThemeValues, resolveToken, mergeTheme } from "../theme/tokens.js";
import { parseTemplate } from "../schema/template.schema.js";
import { issue, type Issue } from "./issues.js";
import { readRegion, type RegionId } from "../editor/tree.js";
import { computePageGeometry } from "../layout/page-geometry.js";

export interface ValidateOptions {
  template: ReportTemplate;
  registry: BlockRegistry;
  dataSources?: DataSource[];
  theme?: Partial<Theme>;
  /** Font families the host guarantees are available (theme fonts are added automatically). */
  availableFonts?: string[];
  /** Skip the JSON shape check (already parsed). */
  skipSchema?: boolean;
}

export interface ValidationResult {
  ok: boolean;
  issues: Issue[];
  errors: Issue[];
  warnings: Issue[];
}

/**
 * Statically analyses a template against a registry, data schemas and a theme.
 * Runs without data and without a DOM, so it can be used server-side before
 * accepting a template for storage.
 */
export function validateTemplate(opts: ValidateOptions): ValidationResult {
  const issues: Issue[] = [];
  const { template, registry } = opts;
  const dataSources = opts.dataSources ?? [];
  const theme = mergeTheme(opts.theme);

  if (!opts.skipSchema) {
    const parsed = parseTemplate(template);
    for (const e of parsed.errors) issues.push(issue("error", "schema-invalid", `${e.path}: ${e.message}`, { region: "document" }));
  }

  if (opts.theme) for (const m of missingThemeValues(opts.theme)) issues.push(issue("warning", "missing-theme-value", `Theme "${opts.theme.name ?? opts.theme.id ?? ""}" has no value for ${m}; the default is used`, { region: "document" }));

  const rootScope = createSchemaScope(dataSources);
  const sourceIds = new Set(dataSources.map((s) => s.id));
  for (const id of template.settings?.dataSources ?? []) if (!sourceIds.has(id)) issues.push(issue("warning", "unknown-binding", `Template was designed against data source "${id}" which is not provided`, { region: "document" }));

  const fonts = new Set<string>([...(opts.availableFonts ?? []), ...(theme.fonts ?? []).map((f) => f.family), ...splitFonts(theme.typography.headingFont), ...splitFonts(theme.typography.bodyFont), ...splitFonts(theme.typography.monoFont ?? "")]);
  const checkFont = (value: string | undefined, where: Partial<Issue>) => {
    if (!value || isTokenRef(value) || !opts.availableFonts) return;
    const families = splitFonts(value);
    if (!families.some((f) => fonts.has(f) || GENERIC_FONTS.has(f.toLowerCase()))) issues.push(issue("warning", "unsupported-font", `Font "${value}" is not available; the theme body font will be used`, where));
  };
  const checkColor = (value: string | undefined, where: Partial<Issue>) => {
    if (!value || !isTokenRef(value)) return;
    if (resolveToken(value, theme) === undefined) issues.push(issue("warning", "missing-theme-token", `Theme token "${value}" does not exist`, where));
  };

  for (const [id, ts] of Object.entries(template.styles.text)) {
    checkFont(ts.fontFamily, { region: "document", prop: `styles.text.${id}.fontFamily` });
    checkColor(ts.color, { region: "document", prop: `styles.text.${id}.color` });
  }

  const geometry = computePageGeometry(template.page);
  if (geometry.contentHeight < 100) issues.push(issue("error", "layout-invalid", "Page margins, header and footer leave less than 100px for content", { region: "document" }));
  if (geometry.contentWidth < 100) issues.push(issue("error", "layout-invalid", "Page margins leave less than 100px of width for content", { region: "document" }));

  const regionOf = (r: RegionId): Issue["region"] => (r === "body" ? "body" : r.startsWith("header") ? "header" : "footer");

  const visitNode = (node: Node, region: RegionId, parent: Node | null, scope: SchemaScope) => {
    const where: Partial<Issue> = { nodeId: node.id, region: regionOf(region) };
    const def = registry.get(node.type);
    if (!def) {
      issues.push(issue("error", "unknown-block", `Block type "${node.type}" is not registered${node.type.includes(".") ? " (custom block unavailable)" : ""}`, where));
      return;
    }
    if (!registry.isTierEnabled(def.license)) issues.push(issue("warning", "license-required", `"${def.name}" requires the ${def.license} tier`, where));

    if (parent) {
      if (!registry.accepts(parent.type, node.type)) issues.push(issue("error", "layout-invalid", `"${def.name}" cannot be placed inside "${registry.get(parent.type)?.name ?? parent.type}"`, where));
    } else if (node.type !== "section") issues.push(issue("error", "layout-invalid", `Top-level nodes must be sections, found "${node.type}"`, where));

    if (node.type === "row") {
      const layout = node.props.layout as number[] | undefined;
      const total = (layout ?? []).reduce((a, b) => a + b, 0);
      if (!layout || total !== 12) issues.push(issue("error", "layout-invalid", `Row layout must sum to 12 (got ${total})`, where));
      else if ((node.children?.length ?? 0) !== layout.length) issues.push(issue("error", "layout-invalid", `Row has ${node.children?.length ?? 0} columns but a ${layout.length}-column layout`, where));
    }

    if (node.style) {
      checkFont(node.style.fontFamily, { ...where, prop: "style.fontFamily" });
      checkColor(node.style.color, { ...where, prop: "style.color" });
      checkColor(node.style.background, { ...where, prop: "style.background" });
      checkColor(node.style.border?.color, { ...where, prop: "style.border.color" });
      if (node.style.textStyle && !template.styles.text[node.style.textStyle]) issues.push(issue("warning", "missing-theme-token", `Text style "${node.style.textStyle}" is not defined in the document styles`, { ...where, prop: "style.textStyle" }));
    }

    const cond = compileCondition(node.visibleWhen);
    if (cond) checkExpr(cond, "visibleWhen", "condition");

    const props = { ...(typeof def.defaultProps === "function" ? def.defaultProps() : def.defaultProps), ...node.props } as Record<string, unknown>;

    function checkExpr(expr: string, prop: string, kind: BindingSpec["kind"], s: SchemaScope = scope, expected?: FieldSchema["type"] | FieldSchema["type"][]): Issue[] {
      const out: Issue[] = [];
      const r = typeCheck(expr, s);
      for (const i of r.issues) {
        const code = i.code === "syntax" ? "invalid-expression" : i.code === "type-mismatch" ? "type-mismatch" : "unknown-binding";
        out.push(issue(i.code === "syntax" ? "error" : "warning", kind === "condition" && code !== "unknown-binding" ? "invalid-condition" : code, `${i.message} in "${expr}"`, { ...where, prop }));
      }
      if (kind === "collection" && r.issues.length === 0 && r.type !== "array" && r.type !== "any" && r.type !== "unknown") out.push(issue("error", "missing-collection", `"${expr}" is a ${r.type}, not a list`, { ...where, prop }));
      if (expected && r.issues.length === 0 && r.type !== "unknown" && r.type !== "any" && r.type !== "null") {
        const list = Array.isArray(expected) ? expected : [expected];
        const numeric = ["number", "currency", "percent"];
        const compatible = list.includes(r.type as FieldSchema["type"]) || (list.some((t) => numeric.includes(t)) && numeric.includes(r.type)) || (list.includes("string") && r.type !== "array" && r.type !== "object");
        if (!compatible) out.push(issue("warning", "type-mismatch", `"${expr}" is a ${r.type}; expected ${list.join(" or ")}`, { ...where, prop }));
      }
      issues.push(...out);
      return out;
    }

    const runSpec = (spec: BindingSpec, target: Record<string, unknown>, prefix: string, s: SchemaScope) => {
      const value = getPath(target, spec.prop);
      const prop = prefix + spec.prop;
      switch (spec.kind) {
        case "value":
          if (typeof value === "string" && value.trim()) checkExpr(value, prop, "value", s, spec.valueType);
          break;
        case "collection":
          if (typeof value === "string" && value.trim()) checkExpr(value, prop, "collection", s);
          else if (def.container?.repeat?.collectionProp === spec.prop) issues.push(issue("warning", "missing-collection", `"${def.name}" has no list to repeat over`, { ...where, prop }));
          else if (def.type === "table" || def.type === "chart") issues.push(issue("warning", "missing-collection", `"${def.name}" is not bound to a list`, { ...where, prop }));
          break;
        case "template":
          if (typeof value === "string") for (const e of extractTemplateTokens(value)) checkExpr(e, prop, "value", s);
          break;
        case "richtext":
          if (isRichTextDoc(value)) for (const e of richTextTokens(value)) checkExpr(e, prop, "value", s);
          break;
        case "condition": {
          const c = compileCondition(value as never);
          if (c) checkExpr(c, prop, "condition", s);
          break;
        }
        case "list":
          break;
      }
    };

    // Scope for scoped bindings (per-item expressions such as table columns)
    let itemScope = scope;
    let childScope = scope;
    const specs = bindingSpecsFor(def);
    // Establish the item scope from collection specs first so per-item bindings can use it regardless of declaration order.
    for (const spec of specs) {
      if (spec.kind !== "collection") continue;
      const collectionExpr = props[spec.prop];
      const itemSchema = typeof collectionExpr === "string" && collectionExpr.trim() ? itemSchemaOf(collectionExpr, scope) : undefined;
      const alias = (spec.alias && (props[spec.alias] as string)) || "item";
      if (dataSources.some((d) => d.id === alias)) issues.push(issue("warning", "scope-shadowing", `Repeater alias "${alias}" hides the data source with the same name`, where));
      const vars: Record<string, FieldSchema> = {
        [alias]: itemSchema ?? { type: "object", label: alias, fields: {} },
        item: itemSchema ?? { type: "object", label: "Item", fields: {} },
        row: itemSchema ?? { type: "object", label: "Row", fields: {} },
        index: { type: "number" },
        first: { type: "boolean" },
        last: { type: "boolean" },
        count: { type: "number" }
      };
      itemScope = scope.child(vars);
      if (def.container?.repeat?.collectionProp === spec.prop) childScope = itemScope;
    }
    for (const spec of specs) {
      if (spec.perItem) {
        runSpec(spec, props, "", itemScope);
        continue;
      }
      if (spec.items) {
        const list = getPath(props, spec.prop);
        if (Array.isArray(list)) list.forEach((item, i) => item && typeof item === "object" && spec.items!.forEach((sub) => runSpec(sub, item as Record<string, unknown>, `${spec.prop}.${i}.`, sub.perItem ? itemScope : scope)));
        continue;
      }
      runSpec(spec, props, "", scope);
      if (spec.kind === "collection") for (const sub of spec.scoped ?? []) runSpec(sub, props, "", itemScope);
    }
    if (def.validate) {
      const vctx: ValidateContext<Record<string, unknown>> = {
        props,
        node,
        schemaScope: itemScope,
        dataSources,
        theme,
        checkExpression: (expr, prop) => checkExpr(expr, prop, "value", itemScope)
      };
      try {
        const extra = def.validate(vctx);
        for (const i of extra) issues.push({ ...i, nodeId: i.nodeId ?? node.id, region: i.region ?? where.region });
      } catch (e) {
        issues.push(issue("warning", "custom", `Validation for "${def.name}" failed: ${(e as Error).message}`, where));
      }
    }

    for (const child of node.children ?? []) visitNode(child, region, node, childScope);
  };

  // Every command, the diff, undo's selection and the canvas find a node by its
  // id, and an index built over duplicates keeps whichever it met last — so a
  // second node with the same id is not cosmetic: editing one edits its twin, or
  // nothing at all. Nothing produced duplicates until templates began arriving
  // from somewhere other than the designer.
  const seen = new Set<string>();
  const checkIds = (nodes: Node[], region: RegionId) => {
    for (const n of nodes) {
      if (seen.has(n.id)) issues.push(issue("error", "duplicate-id", `Two blocks share the id "${n.id}"`, { nodeId: n.id, region: regionOf(region) }));
      seen.add(n.id);
      checkIds(n.children ?? [], region);
    }
  };
  for (const region of ["body", "header", "header:first", "footer", "footer:first"] as const) checkIds(readRegion(template, region), region);

  const visitRegion = (nodes: Node[], region: RegionId) => nodes.forEach((n) => visitNode(n, region, null, rootScope));
  visitRegion(template.body, "body");
  visitRegion(template.header.default, "header");
  visitRegion(template.header.firstPage ?? [], "header:first");
  visitRegion(template.footer.default, "footer");
  visitRegion(template.footer.firstPage ?? [], "footer:first");

  if (template.body.every((s) => (s.children?.length ?? 0) === 0)) issues.push(issue("info", "empty-content", "The document has no content yet", { region: "body" }));

  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity === "warning");
  return { ok: errors.length === 0, issues, errors, warnings };
}

const GENERIC_FONTS = new Set(["serif", "sans-serif", "monospace", "system-ui", "ui-sans-serif", "ui-serif", "ui-monospace", "cursive", "fantasy", "inherit"]);

function splitFonts(value: string): string[] {
  return value
    .split(",")
    .map((f) => f.trim().replace(/^['"]|['"]$/g, ""))
    .filter(Boolean);
}

function getPath(obj: Record<string, unknown>, path: string): unknown {
  let cur: unknown = obj;
  for (const p of path.split(".")) {
    if (cur === null || cur === undefined || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

export function itemSchemaOf(collectionExpr: string, scope: SchemaScope): FieldSchema | undefined {
  try {
    const ast = parseExpression(collectionExpr);
    const base = ast.kind === "pipe" ? unwrapPipes(ast) : ast;
    if (base.kind !== "path") return undefined;
    const r = resolvePathSchema(base.segments, scope);
    return r.schema?.type === "array" ? r.schema.itemSchema : undefined;
  } catch {
    return undefined;
  }
}

function unwrapPipes(e: import("../expr/ast.js").Expr): import("../expr/ast.js").Expr {
  let cur = e;
  while (cur.kind === "pipe") cur = cur.input;
  return cur;
}

/** URL safety check shared by validation and renderers. */
export function isSafeUrl(url: string | undefined, opts: { allowData?: boolean } = {}): boolean {
  if (!url) return false;
  const u = url.trim();
  if (/^https?:\/\//i.test(u)) return true;
  if (/^mailto:/i.test(u)) return true;
  if (u.startsWith("/") && !u.startsWith("//")) return true;
  if (u.startsWith("./") || u.startsWith("../")) return true;
  if (opts.allowData && /^data:image\/(png|jpe?g|gif|webp|svg\+xml)[;,]/i.test(u)) {
    // Only SVG is markup, and only markup can carry script: a PNG or a JPEG is
    // decoded by the image pipeline and never parsed as a document. Returning
    // here also keeps the scan off the large payloads, which are always raster.
    if (!/^data:image\/svg\+xml/i.test(u)) return true;

    const comma = u.indexOf(",");
    const payload = comma === -1 ? "" : u.slice(comma + 1);
    let decoded: string;
    if (/;base64/i.test(u.slice(0, comma === -1 ? u.length : comma))) {
      // This branch used to be missing, and it is the one that matters:
      // `FileReader.readAsDataURL` — how every uploaded file arrives — always
      // produces base64, so scanning the undecoded text found nothing and a
      // scripted SVG was declared safe. The percent-encoded form was rejected
      // correctly all along, which is what made it easy to miss.
      decoded = decodeBase64(payload);
    } else {
      try {
        decoded = decodeURIComponent(payload);
      } catch {
        decoded = payload;
      }
    }
    // Deliberately the whole payload, not a prefix. A cap only decides how far
    // into the file an attacker has to put the script.
    return !/<script|javascript:|on[a-z]+\s*=|<foreignObject/i.test(decoded);
  }
  return false;
}

/** Base64 to text, in whichever runtime this is: browsers, workers and Node all differ. */
function decodeBase64(value: string): string {
  const b64 = value.trim();
  try {
    const g = globalThis as { atob?: (s: string) => string; Buffer?: { from(s: string, enc: string): { toString(enc: string): string } } };
    if (typeof g.atob === "function") return g.atob(b64);
    if (g.Buffer) return g.Buffer.from(b64, "base64").toString("utf8");
  } catch {
    /* malformed base64 falls through */
  }
  // Undecodable means unverifiable, and an image that cannot be checked is not
  // one to declare safe.
  return "<script";
}
